/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { MutateMenusAndPushToSalesChannelsModal } from "./MutateMenusAndPushToSalesChannelsModal";
import { ButtonPrimary } from "../../../components/NewButtons";
import * as authSelectors from "../../auth/authSelectors";
import { FormProvider, useForm } from "react-hook-form";
import { StyledForm } from "../../../components/StyledForm";
import { PublishMenusToDestinations } from "../models/Menus";
import { toastr } from "react-redux-toastr";
import { useSelector } from "react-redux";
import { ConfirmMenuPushModal } from "./ConfirmMenuPushModal";
import {
  useBulkPushMenusForSalesChannels,
  useGetPublishDestinations,
  usePublishMenusToDestinations,
} from "../service/menus-api";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { css } from "@emotion/react";
import { breakpoints } from "../../../components/breakpoints";

const formStyle = css`
  ${breakpoints.tablet} {
    width: fit-content;
  }
`;

export type MutateMenusOnSalesChannelsFormValues = {
  destinations: Array<{
    id: string;
    menus: string[];
  }>;
};

export const MutateMenusOnSalesChannelsSelector = () => {
  const [visibleModal, setVisibleModal] = useState<
    "table" | "confirm" | undefined
  >(undefined);
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const methods = useForm<MutateMenusOnSalesChannelsFormValues>({
    defaultValues: {
      destinations: [],
    },
  });

  const {
    mutateAsync: addMenusToSalesChannels,
    isLoading: submitIsLoading,
  } = usePublishMenusToDestinations();

  const {
    mutateAsync: bulkPushMenusToDestinations,
    isLoading: pushMenuIsLoading,
  } = useBulkPushMenusForSalesChannels();

  const {
    data: publishDestinations,
    isLoading,
    error: getPublishDestinationsError,
  } = useGetPublishDestinations(organizationKeyName, visibleModal === "table");

  // Using destinations that are physical shops or children of physical shops
  const filteredPublishDestinations = useMemo(() => {
    const physicalDestinationIds = new Set(
      publishDestinations?.destinations
        ?.filter((d) => d.shopType === "physical")
        .map((d) => d.id)
    );

    const destinations =
      publishDestinations?.destinations?.filter(
        (d) =>
          d.shopType === "physical" ||
          (d.parent && physicalDestinationIds.has(d.parent))
      ) || [];

    return {
      destinations,
      availableMenus: publishDestinations?.availableMenus || [],
    };
  }, [publishDestinations]);

  const { reset } = methods;

  const onSubmit = async (values: MutateMenusOnSalesChannelsFormValues) => {
    const destinations = values.destinations
      .filter((des) => {
        // If no menu is selected, we don't want to set it or push it
        if (des.menus.length === 0) {
          return false;
        }

        // If first element is undefined, we treat it as no menu is set
        if (des.menus[0] === undefined) {
          return false;
        }

        // If the menu is different from the original, we want to set it
        const originalDestination = filteredPublishDestinations?.destinations.find(
          (d) => d.id === des.id
        );
        // We assume one menu per destination. TODO: Support multiple menus on one destination
        const originalMenus = originalDestination?.currentMenus.find(
          (m) => m.id === des.menus[0]
        );
        return originalMenus?.id !== des.menus[0];
      })
      // If we want to remove a menu, we set it to empty string in form and we have to remove that to get an empty array
      .map((des) => ({
        id: des.id,
        menus: des.menus.filter((m) => m !== ""),
      }));
    const request: PublishMenusToDestinations = {
      destinations,
      organizationKeyName,
    };

    try {
      // Setting menus
      await addMenusToSalesChannels(request);

      // Closing modal and resetting form, and let push be done in background
      setVisibleModal(undefined);

      // Pushing menus
      await bulkPushMenusToDestinations({
        organizationKeyName,
        data: {
          destinations: destinations.map((d) => d.id),
        },
      });
      toastr.success(
        "Success",
        "Menus have been updated successfully and pushed to sales channels."
      );
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useEffect(() => {
    if (!filteredPublishDestinations || isLoading) {
      return;
    }

    reset({
      destinations: filteredPublishDestinations.destinations.map((s) => ({
        id: s.id,
        menus: s.currentMenus.map((m) => m.id),
      })),
    });
  }, [filteredPublishDestinations, isLoading, reset]);

  useHandleFetchError(getPublishDestinationsError);

  return (
    <FormProvider {...methods}>
      <StyledForm css={formStyle} width="auto">
        <ButtonPrimary onClick={() => setVisibleModal("table")} type="button">
          Push menus to sales channels
        </ButtonPrimary>
        <MutateMenusAndPushToSalesChannelsModal
          isOpen={visibleModal === "table"}
          onClose={() => {
            reset();
            setVisibleModal(undefined);
          }}
          publishDestinations={filteredPublishDestinations}
          isLoading={isLoading}
          onSave={() => setVisibleModal("confirm")}
        />
        <ConfirmMenuPushModal
          isOpen={visibleModal === "confirm"}
          onClose={() => setVisibleModal("table")}
          publishDestinations={filteredPublishDestinations}
          onSubmit={methods.handleSubmit(onSubmit)}
          submitIsLoading={submitIsLoading || pushMenuIsLoading}
        />
      </StyledForm>
    </FormProvider>
  );
};
